import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// internal import
import apiLink from "../../../api/apiLink";
import Loader from "../../../components/common/Loader";
import { smsCount } from "../../../components/common/UtilityMethods";
import { smsSettingUpdateIsp } from "../../../features/authSlice";
import useISPowner from "../../../hooks/useISPOwner";
import AudioUploadPlayer from "./AudioUploadPlayer";
import { audioFileUpload } from "../../../features/apiCallAdmin";

const MAX_SMS_LENGTH = 334;

const ITEM_GROUP_ONE = [
  { id: "USERNAME", value: "USER: USERNAME" },
  { id: "CUSTOMER_ID", value: "ID: CUSTOMER_ID" },
  { id: "CUSTOMER_NAME", value: "NAME: CUSTOMER_NAME" },
  { id: "AMOUNT", value: "BILL: AMOUNT" },
];

const ITEM_GROUP_TWO = [
  { id: "BILL_DUE", value: "DUE: BILL_DUE" },
  { id: "BILL_NOTE", value: "NOTE: BILL_NOTE" },
  { id: "BILL_MONTH", value: "MONTH: BILL_MONTH" },
  { id: "BILL_NOTE_DATE", value: "DATE: BILL_NOTE_DATE" },
  { id: "PAID_AT", value: "PAID AT: PAID_AT" },
];

const ALLOWED_ITEMS = [...ITEM_GROUP_ONE, ...ITEM_GROUP_TWO].map(
  (i) => i.value,
);

function BillConfirmationSmsTemplate() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //---> @Get user & current user data form useISPOwner hooks
  const { ispOwnerId, bpSettings, settings } = useISPowner();

  const [loading, setLoading] = useState(false);

  const [messageType, setMessageType] = useState("");
  const [sendingType, setSendingType] = useState("");
  const [billConfirmation, setBillConfirmation] = useState("off");

  // text template
  const [fontText, setFontText] = useState("");
  const [bottomText, setBottomText] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);

  const [audioFile, setAudioFile] = useState(null);
  const savedAudio = settings?.sms?.voiceTemplate?.billConfirmation;

  const messageTypes = useMemo(
    () => [
      { label: t("text"), value: "text" },
      ...(bpSettings?.voiceMessage
        ? [{ label: t("voice"), value: "voice" }]
        : []),
    ],
    [t],
  );

  const sendingMessageTypes = useMemo(
    () => [
      { label: t("nonMasking"), value: "nonMasking" },
      { label: t("masking"), value: "masking" },
      { label: t("fixedNumber"), value: "fixedNumber" },
      ...(bpSettings?.hasSmsGateway
        ? [{ label: t("personalGateway"), value: "personalGateway" }]
        : []),
    ],
    [t, bpSettings?.hasSmsGateway],
  );

  const upperText = useMemo(
    () => selectedItems.map((item) => `\n${item}`).join(""),
    [selectedItems],
  );

  const fullText = fontText + upperText + bottomText;
  const bottomMaxLength = Math.max(
    0,
    MAX_SMS_LENGTH + 1 - fontText.length - upperText.length,
  );

  useEffect(() => {
    const sms = settings?.sms;
    const raw = sms?.template?.billConfirmation || "";

    const items = raw
      .split("\n")
      .filter((line) => ALLOWED_ITEMS.includes(line));
    let body = raw;
    items.forEach((item) => {
      body = body.replace(item, "");
    });

    const lines = body.split("\n");
    const bottom = lines
      .slice(2)
      .filter((line) => line !== "")
      .map((line) => line + "\n")
      .join("");

    setSelectedItems(items);
    setFontText(lines[0] || "");
    setBottomText(bottom);

    setBillConfirmation(sms?.billConfirmation ? "on" : "off");
    setMessageType(sms?.billConfirmationMessageType || "");
    setSendingType(sms?.billConfirmationSendBy || "");
  }, [settings]);

  const itemSettingHandler = (item) => {
    if (selectedItems.includes(item)) {
      setSelectedItems((prev) => prev.filter((i) => i !== item));
      return;
    }

    if ((fullText + "\n" + item).length > MAX_SMS_LENGTH) {
      toast.error(t("exceedSMSLimit"));
      return;
    }
    setSelectedItems((prev) => [...prev, item]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!messageType) {
      toast.error(t("messageType"));
      return;
    }

    const newFile = messageType === "voice" ? audioFile : null;

    if (messageType === "voice" && !newFile && !savedAudio?.path) {
      toast.error("Voice file is required.");
      return;
    }

    const data = {
      ...settings?.sms,
      billConfirmationMessageType: messageType,
      billConfirmationSendBy: sendingType,
      billConfirmation:
        billConfirmation === "on"
          ? true
          : billConfirmation === "off"
            ? false
            : null,
    };

    if (messageType === "text") {
      data.template = {
        ...settings?.sms?.template,
        billConfirmation: `${fontText}${upperText}\n${bottomText}`,
      };
    }

    setLoading(true);
    try {
      if (newFile) {
        const formData = new FormData();
        formData.append("file", newFile);

        const uploaded = await audioFileUpload(setLoading, formData);
        const f = uploaded?.[0];

        if (!f) {
          toast.error("Voice file upload failed.");
          return;
        }
        setLoading(true);

        data.voiceTemplate = {
          ...settings?.sms?.voiceTemplate,
          billConfirmation: {
            originalName: f.originalName,
            path: f.path,
            publicID: f.publicID,
            duration: f.duration,
          },
        };
      }

      const res = await apiLink.patch(
        `/ispOwner/settings/sms/${ispOwnerId}`,
        data,
      );
      dispatch(smsSettingUpdateIsp(res.data));
      if (newFile) setAudioFile(null);

      toast.success(t("billConfirmationSaveSuccess"));
    } catch (error) {
      console.error("SMS settings update failed:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update SMS settings.",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderItem = (item) => (
    <div className="radioselect" key={item.id}>
      <input
        id={`bc-item-${item.id}`}
        type="checkbox"
        className="getValueUsingClass"
        value={item.value}
        checked={selectedItems.includes(item.value)}
        onChange={(e) => itemSettingHandler(e.target.value)}
      />
      <label className="templatelabel" htmlFor={`bc-item-${item.id}`}>
        {item.value}
      </label>
    </div>
  );

  return (
    <div>
      <form onSubmit={handleSubmit} className="settingForm">
        <div className="writeMessageSection displayGrid">
          <div className="messageStatus d-flex justify-content-between">
            <div className="message-sending-type">
              <h5>{t("messageType")}</h5>
              <div className="smsType">
                {messageTypes.map((type) => (
                  <div className="message_radio" key={type.value}>
                    <input
                      type="radio"
                      id={`bc-messageType-${type.value}`}
                      name="billConfirmationMessageType"
                      value={type.value}
                      onChange={(e) => setMessageType(e.target.value)}
                      checked={messageType === type.value}
                    />
                    <label htmlFor={`bc-messageType-${type.value}`}>
                      {type.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="sending-status">
              <h5> {t("billConfirmSMStemplate")} </h5>
              <div className="displayGrid1 mb-3">
                {[
                  { id: "bc-onTemplate", value: "on", label: t("ON") },
                  { id: "bc-offTemplate", value: "off", label: t("OFF") },
                ].map((opt) => (
                  <div className="message_radio" key={opt.id}>
                    <input
                      type="radio"
                      name="billConfirmation"
                      id={opt.id}
                      value={opt.value}
                      checked={billConfirmation === opt.value}
                      onChange={(e) => setBillConfirmation(e.target.value)}
                    />
                    <label htmlFor={opt.id}>{opt.label}</label>
                  </div>
                ))}
              </div>
            </div>

            {messageType === "text" && (
              <div className="message-sending-type">
                <h5>{t("sendingMessageType")}</h5>
                <div className="smsType">
                  {sendingMessageTypes.map((type) => (
                    <div className="message_radio" key={type.value}>
                      <input
                        type="radio"
                        id={`bc-sendingType-${type.value}`}
                        name="billConfirmationSendBy"
                        value={type.value}
                        onChange={(e) => setSendingType(e.target.value)}
                        checked={sendingType === type.value}
                      />
                      <label htmlFor={`bc-sendingType-${type.value}`}>
                        {type.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {messageType === "text" ? (
            <>
              <input
                value={fontText}
                onChange={(e) => setFontText(e.target.value)}
                className="form-control"
                type="text"
                placeholder={t("title")}
                maxLength={40}
              />

              <div className="billconfirm">
                <div className="showthesequence">
                  <p className="endingText">{fontText}</p>
                  {selectedItems.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                  <p className="endingtext">{bottomText}</p>
                </div>

                <div className="d-flex">
                  <div className="displayFlexx">
                    {ITEM_GROUP_ONE.map(renderItem)}
                  </div>
                  <div>
                    <div>{ITEM_GROUP_TWO.map(renderItem)}</div>
                  </div>
                </div>
              </div>

              <div className="smsCount">
                <span className="smsLength">
                  {t("letter")} {fullText.length}
                </span>
                <span>SMS: {smsCount(fullText)}</span>
              </div>

              <textarea
                id="bc-messageTextArea"
                rows="6"
                className="form-control"
                placeholder={t("messageLikhun")}
                value={bottomText}
                maxLength={bottomMaxLength}
                onChange={(e) => setBottomText(e.target.value)}
              ></textarea>
            </>
          ) : messageType === "voice" ? (
            <AudioUploadPlayer
              file={audioFile}
              savedAudio={savedAudio}
              onFileChange={setAudioFile}
            />
          ) : null}
        </div>

        <hr />
        <footer>
          <button type="submit" className="btn btn-success" disabled={loading}>
            {loading ? <Loader /> : t("save")}
          </button>
        </footer>
      </form>
    </div>
  );
}

export default BillConfirmationSmsTemplate;
